import {IReg, IRegPointer, IRegsPointer, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IWspUiEnv, JInfoWsp, Wsp, WSP} from "/@lib@/wsp/wsp.js";
import {
	BoolCheckInputArea,
	ControlArea,
	ControlAsyncArea,
	Dataset,
	DatasetEntry,
	IControlAreaContext,
	IControlLabelContext,
	InputArea,
	InputChoiceArea,
	InputTextArea,
	LabelArea,
	LabelAsyncArea,
	StringInputArea
} from "/@lib@/commons/areas.js";
import {WspTypeSelector} from "/@back@/wsp/widgets/wspType/wspTypeSelector.js";
import {BaseElementAsync, MsgLabel, OLabelInit, OSkinableInit} from "/@back@/commons/basis.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {SkinSetSelector} from "/@back@/wsp/widgets/skinSet/skinSetSelector.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {IFormJsonisable} from "/@lib@/commons/forms.js";
import {ActionBtn, OActionBtnInit} from "/@back@/commons/widgets/buttons.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {AutoMigrWspAction, ReloadWspAction, RemoveCacheWspAction} from "/@back@/wsp/actions/wspActions.js";
import {WSP_DRV} from "/@lib@/wsp/drv.js";
import {IO} from "/@lib@/commons/io/io.js";
import {OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {Action} from "/@lib@/commons/actions.js";
import {InputOrderedSetPanel} from "/@back@/commons/widgets/inputs.js";
import {WSP_L10N} from "/@lib@/wsp/l10n.js";

/**
 * Area affichant une propriété d'un WSP
 */
export abstract class WspArea<C extends IControlLabelContext = IControlLabelContext> extends LabelArea<IControlLabelContext & IRegPointer<IWspUiEnv>> {

	isVisible(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>): boolean {
		if (!ctx.reg.env.wsp) return false;
		return super.isVisible(ctx);
	}
}

/**
 * Code de l'atelier
 */
class WspCodeInputArea extends StringInputArea<IRegPointer<IWspUiEnv>> {

	constructor() {
		super("code", "text");
		this.setLabel("Code");
		this.setPattern("[a-zA-Z0-9_]+");
		this.setRequired(true);
		this.setInputCustomValidity(async function (this: HTMLInputElement, ctx: IRegPointer<IWspUiEnv>): Promise<string> {
			if (!this.checkValidity()) return "Seuls les caractères alpha-numériques et _ sont autorisés";
			if (this.value) {
				const code = this.value;
				let infoWsp = await ctx.reg.env.universe.wspServer.config.adminWspUrl.fetchJson<JInfoWsp>(IO.qs("cdaction", "InfoWsp", "param", code));
				if (infoWsp && infoWsp.status != 'noWsp') {
					return `L'atelier avec le code '${code}' existe déjà.`;
				}
			}
			return "";
		}, 500, true)
	}

	_buildControl(ctx: IRegPointer<IWspUiEnv>, name: string): HTMLInputElement {
		let input = super._buildControl(ctx, name);
		DOM.setAttr(input, "spellcheck", "false");
		DOM.setAttr(input, "autocomplete", "current-password");// "off" ne fonctionne pas
		return input;
	}

}

class WspCodeArea extends WspArea {

	constructor() {
		super("code");
		this.setLabel("Code");
	}

	_buildControl(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>, name: string): HTMLElement {
		return <div>{ctx.reg.env.wsp.code}</div>;
	}

}

/**
 * Statut de l'atelier
 * 	Note : l'atelier est déjà dans un contexte loaded
 */
class WspStatusArea extends WspArea {

	constructor() {
		super("code");
		this.setLabel("Statut");
	}

	_buildControl(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>, name: string): HTMLElement {
		return <WspStatus î={{reg: ctx.reg}}/>;
	}

}

export class WspStatus extends BaseElementAsync implements IFormJsonisable {
	protected reg: IReg<IWspUiEnv>;
	protected labelElt: MsgLabel;
	protected migrlBtn: ActionBtn<IRegPointer<IWspUiEnv>>;

	protected async _initialize(init: OSkinableInit & IRegPointer<IWspUiEnv>): Promise<any> {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.reg = REG.findReg(this, init);
		this.labelElt = sr.appendChild(<MsgLabel class="status multiline" î={{label: "Chargement de l'atelier en cours..."} as OLabelInit}/>) as MsgLabel;
		sr.appendChild(<div class="spacer"/>);
		this.migrlBtn = sr.appendChild(<ActionBtn
			î={{reg: init.reg, actionContext: init, label: "Migrer", action: new AutoMigrWspAction(), uiContext: "custom"} as OActionBtnInit<IRegPointer<IWspUiEnv>>}/>) as ActionBtn<IRegPointer<IWspUiEnv>>;
		await this._refreshAll();
	}

	protected async _refreshAll(): Promise<void> {
		let infoWsp = this.reg.env.wsp.infoWsp || this.reg.env.wsp.infoWspError;
		if (this.reg.env.wsp.migrating) {
			this.labelElt.setCustomMsg("Migration de l'atelier en cours...", "info");
		} else if (infoWsp?.isMigrationNeeded) {
			this.labelElt.setCustomMsg("Migration des contenus nécessaire", "warning");
		} else if (this.reg.env.wsp.isDeleted === true) {
			this.labelElt.setCustomMsg("Atelier supprimé", "error");
		} else if (this.reg.env.wsp.isAccessDenied === true) {
			this.labelElt.setCustomMsg("Accès non autorisé", "error");
		} else if (this.reg.env.wsp.isInError === true) {
			if (this.reg.env.wsp.infoWsp && !this.reg.env.wsp.wspMetaUi)
				this.labelElt.setCustomMsg("Échec au chargement du modèle documentaire.\nVeuillez sélectionner ou installer un modèle compatible.", "warning");
			else if (this.reg.env.wsp.infoWspError?.status === "failedNoData")
				this.labelElt.setCustomMsg("Contenus de l'atelier supprimés", "error");
			else
				this.labelElt.setCustomMsg("Atelier en erreur", "error");
		} else if (this.reg.env.wsp.wspMetaUi === null) {
			this.labelElt.setCustomMsg("Accès impossible aux propriétés de l'atelier", "error");
		} else if (this.reg.env.wsp.fullLoading) {
			this.labelElt.setCustomMsg("Chargement de l'atelier en cours...", "info");
		} else {
			this.labelElt.setCustomMsg("Opérationnel");
		}
		this.migrlBtn.refresh();
	}

	extractJson(parent: Dict<any>): boolean {
		this.reg.env.wsp.waitForLoad().then(() => {
			this._refreshAll();
		});
		return false;
	}

	fillJson(parent: any, root: any): void {
		//NA
	}
}

customElements.define('wsptype-status', WspStatus);
REG.reg.registerSkin('wsptype-status', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
	}

	c-msg {
		flex: unset;
	}

	.spacer {
		flex: 1;
	}
`);


/**
 * Titre de l'atelier
 */
class WspTitleInputArea extends StringInputArea<IRegPointer<IWspUiEnv>> {
	constructor(protected onlyIfNotEmpty = false) {
		super("title", "text");
		this.setLabel("Titre");
	}

	_buildControl(ctx: IRegPointer<IWspUiEnv>, name: string): HTMLInputElement {
		const ctrl = super._buildControl(ctx, name);
		if (this.onlyIfNotEmpty) {
			// permet une gestion de construction de titre par défaut si non spécifié (ex : création d'un wsp depuis un scar)
			Object.assign(ctrl, {
				fillJson: function (parent: Jsonisable, root: Jsonisable) {
					if (ctrl.value)
						parent[ctrl.name] = ctrl.value;
				},
				extractJson: function (parent: Dict<any>) {
					const value = parent[ctrl.name];
					if (value) {
						ctrl.value = parent[ctrl.name];
						return true;
					}
					return false;
				}
			} as IFormJsonisable);
		}
		return ctrl;
	}
}

/**
 * Description de l'atelier
 */
class WspDescInputArea extends InputTextArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("desc");
		this.setLabel("Description");
	}

	_buildControl(ctx: IRegPointer<IWspUiEnv>, name: string): HTMLTextAreaElement {
		let textElt = super._buildControl(ctx, name);
		DOM.setAttr(textElt, "rows", "1");
		return textElt;
	}
}

/**
 * Active la fonction l10n
 */
class WspL10nInputArea extends BoolCheckInputArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super(WSP_L10N.WSP_PARAM_L10N);
		this.setLabel("Fonctions de traduction" + " (expérimental)");//splitté pour permettre de virer le suffixe sans avoir à retraduire le terme
		this.setDescription("Activer les fonctions graphiques dédiées à la traduction");
		this.setCheckedValue("true");
		this.setUncheckedValue(null);
	}

	isVisible(ctx: IRegPointer<IWspUiEnv>): boolean {
		if (!ctx.reg.env.wsp || !ctx.reg.env.wsp.hasFeature('drvL10n')) return false;
		const infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		if (!infoWsp || !WSP_DRV.isDrv(infoWsp.props)) return false;
		//Evol : proposer sur drf si master drf avec l10n activé
		return super.isVisible(ctx);
	}
}


/**
 * Alias de l'atelier
 */
export class WspAliasInputArea extends StringInputArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("alias", "text");
		this.setLabel("Alias de l'atelier");
		this.setPattern("^(?!(^[0-9A-Za-z]{22}$))[A-Za-z0-9_\\-]*$");//cf eu.scenari.wspodb.wsp.OdbWspDefinition.sWspAliasCodePattern
		this.isReadOnly = updatePropReadonly
	}
}

/**
 * [DRF] Titre de l'atelier
 */
class WspDraftTitleInputArea extends StringInputArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("draftTitle", "text");
		this.setLabel("Titre du calque de travail");
	}
}

/**
 * [DRF] Master
 */
class WspDrfRefWspTitleArea extends LabelAsyncArea<IRegPointer<IWspUiEnv> & IControlLabelContext> {

	constructor() {
		super("wspMaster");
		this.setLabel("");
	}

	async _loadControl(ctx: IRegPointer<IWspUiEnv> & IControlLabelContext, name: string): Promise<HTMLElement> {
		let infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		let str;
		if (infoWsp) {
			let masterWspCd = infoWsp.props.drfRefWsp;
			let masterWsp = new Wsp(ctx.reg.env.wsp.wspServer, masterWspCd);
			await masterWsp.waitForLoad();
			const wspTitle = masterWsp.wspTitle;
			str = `Calque de travail de '${wspTitle}'`;
		} else
			str = 'Calque de travail';
		return <div><span class="important">{str}</span></div>;
	}

}

class WspDrfRefWspArea extends LabelAsyncArea<IRegPointer<IWspUiEnv> & IControlLabelContext> {

	constructor() {
		super("wspMaster");
		this.setLabel("Atelier maitre");
	}

	async _loadControl(ctx: IRegPointer<IWspUiEnv> & IControlLabelContext, name: string): Promise<HTMLElement> {
		let infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		if (infoWsp) {
			let masterWspCd = infoWsp.props.drfRefWsp;
			return <div>{masterWspCd}</div>;
		}
	}

}

/**
 * [DRV] master
 */
class WspDrvMasterTitleArea extends LabelAsyncArea<IRegPointer<IWspUiEnv> & IControlLabelContext> {

	constructor() {
		super("wspMaster");
		this.setLabel("");
	}

	async _loadControl(ctx: IRegPointer<IWspUiEnv> & IControlLabelContext, name: string): Promise<HTMLElement> {
		const infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		const wspDefProps = ctx.reg.env.wsp.wspDefProps;
		const isWspL10n = WSP_L10N.isL10n(wspDefProps);
		let str;
		if (infoWsp) {
			let masterWspCd = ctx.reg.env.wsp.infoWsp.props.drvMasterWsp;
			let masterWsp = new Wsp(ctx.reg.env.wsp.wspServer, masterWspCd);
			await masterWsp.waitForLoad();
			const wspTitle = masterWsp.wspTitle;
			//str = isWspL10n ? `Traduction de '${wspTitle}'` : `Dérivation de '${wspTitle}'`;
			str = `Dérivation de '${wspTitle}'`;
		} else {
			//str = isWspL10n ? 'Traduction' : 'Dérivation';
			str = 'Dérivation';
		}
		return <div><span class="important">{str}</span></div>;
	}

}

class WspDrvMasterArea extends LabelAsyncArea<IRegPointer<IWspUiEnv> & IControlLabelContext> {

	constructor() {
		super("wspMaster");
	}

	getLabel(ctx: IRegPointer<IWspUiEnv> & IControlLabelContext): string {
		const wspDefProps = ctx.reg.env.wsp.wspDefProps;
		const isWspL10n = WSP_L10N.isL10n(wspDefProps);
		return isWspL10n ? "Atelier source de traduction" : "Atelier source de dérivation";
	}

	async _loadControl(ctx: IRegPointer<IWspUiEnv> & IControlLabelContext, name: string): Promise<HTMLElement> {
		let infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		if (infoWsp) {
			let masterWspCd = infoWsp.props.drvMasterWsp;
			return <div>{masterWspCd}</div>;
		}
	}

}

/**
 * [DRV] Sources de dérivation
 */
class WspDrvDefaultSrcFindPathInputArea<C extends IRegPointer<IWspUiEnv> & IControlAreaContext> extends ControlAsyncArea<C> {
	constructor() {
		super("drvDefaultSrcFindPath");
		this._label = "Sources de dérivation";
		this._description = "Sources intermédiaires de dérivation (optionnel)";
	}

	isEnabled(ctx: C): boolean {
		let infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		if (!infoWsp) return false;
		return super.isEnabled(ctx);
	}

	async _loadControl(ctx: C, name: string): Promise<InputOrderedSetPanel> {
		// Liste des valeurs
		const infoWsp = ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError;
		const dataSet: Dataset<DatasetEntry<IWspUiEnv & IControlAreaContext>> = {};
		if (infoWsp) {
			const currentDrvAxis = WSP_DRV.isDrv(infoWsp.props) ? infoWsp.props.drvAxis : null;
			const masterWspCd = WSP_DRV.isDrv(infoWsp.props) ? infoWsp.props.drvMasterWsp : null;

			// Refresh utile de l'infoWsp car ces propriétés ne sont pas dynamiquement rafraichies ou absentes
			const masterInfoWsp = await ctx.reg.env.wsp.wspServer.config.adminWspUrl.fetchJson<JInfoWsp>(IO.qs("cdaction", "InfoWsp", "param", masterWspCd || ctx.reg.env.wsp.code, "wspPropsOpts", "(drvWspUsedBy 'true' listDrvs 'true')"));
			if (masterInfoWsp.props.listDrvs)
				masterInfoWsp.props.listDrvs.split(" ").forEach((drvAxis: string) => {
					// FIXME : récupérer le titre des ateliers
					if (!currentDrvAxis || drvAxis !== currentDrvAxis) dataSet[drvAxis] = {label: drvAxis};
				});
		}
		// Input
		const inputElt = new InputOrderedSetPanel().initialize({dataSet, selectionLabel: "Sources", availableLabel: "", emptySelectionMsg: "Aucune source", name});
		inputElt.title = this.getDescription(ctx);
		const defaultValue = this.getDefaultValue(ctx);
		if (defaultValue != null) inputElt.value = defaultValue;
		return inputElt;
	}

}

/**
 * [DRV] axis
 * Contexte : atelier master
 */
class WspDrvAxisInputArea extends StringInputArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("drvAxis", "text");
		this._label = "Code de dérivation";
		this.setPattern("[a-zA-Z0-9\\-_]+");
		this.setInputCustomValidity(async function (this: HTMLInputElement, ctx: IRegPointer<IWspUiEnv>): Promise<string> {
			// Refresh utile de l'infoWsp car ces propriétés de sont pas dynamiquement rafraichies
			let infoWsp = await ctx.reg.env.wsp.wspServer.config.adminWspUrl.fetchJson<JInfoWsp>(IO.qs("cdaction", "InfoWsp", "param", ctx.reg.env.wsp.code, "wspPropsOpts", "(drvWspUsedBy 'true' listDrvs 'true' listDrfs 'true')"));
			if (!this.checkValidity()) return "Seuls les caractères alpha-numériques, tiret et underscore sont autorisés";
			const code = this.value;
			if (infoWsp && infoWsp.props.listDrvs && infoWsp.props.listDrvs.split(" ").indexOf(this.value) > -1) return `Le code de dérivation '${code}' a déjà été utilisé.`;
			return "";
		}, 500);
	}

}


/**
 * [création] Sélection d'un scar/scwsp
 */
class WspSelectScarInputArea extends InputArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("scwspFile");
		this.setLabel("Archive à importer");
		this.setInputType("file");
	}

	_buildControl(ctx: IRegPointer<IWspUiEnv>, name: string): HTMLInputElement {
		let inputElt = super._buildControl(ctx, name);
		inputElt.type = "file";
		inputElt.accept = ".scar,.scwsp";
		inputElt.multiple = false;
		return inputElt;
	}
}

/**
 * [suppression] répertoire de génération
 */
class WspDeleteGenInputArea extends BoolCheckInputArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("deleteGen");
		this.setLabel("Supprimer le répertoire de génération");
		this.setDefaultValue(true);
	}
}

/**
 * [suppression] répertoire de data FS
 */

class WspDeleteFsInputArea extends BoolCheckInputArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("deleteContent");
		this.setLabel("Supprimer le répertoire des données");
		this.setDefaultValue(false);
	}
}

/*
class WspDeleteFsInputAreaZZZ extends InputChoiceArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("deleteContent", "checkbox");
		this.setLabel("Supprimer le répertoire des données").setDataset({"true": {label: ""}});
	}

	//public _buildControl(ctx: IRegPointer<IWspUiEnv>, name: string): HTMLDivElement {
	protected _buildControlLabelSSS(control: HTMLElement, ctx: C, label = this.getLabel(ctx)): HTMLDivElement {
		const rootElt = super._buildControl(ctx, name);
		const labelElt = rootElt.getElementsByTagName('label')[0];
		if(ctx.reg.env.universe.config.local){
			let infoWsp = ctx.reg.env.wsp ? (ctx.reg.env.wsp.infoWsp || ctx.reg.env.wsp.infoWspError) : null;
			const path = infoWsp ? infoWsp.content : "";
			if(path){
				labelElt.appendChild(<FileSystemPath î={{
					openBtn : true,
					type: "dir",
					label :path,
				} as OFileSystemPathInit}/>) as FileSystemPath;
			}
		}
		return rootElt;
	}
}
*/


/**
 * [db] options
 */
class WspOptionsInputArea extends ControlArea<IControlLabelContext & IRegPointer<IChainEnv>> {
	constructor(protected noPublicWsp = false, protected noExtIt = false, protected noAirIt = false) {
		super("wspOptions");
		this.setLabel("Options");
		this.setReadOnly(updatePropReadonly)
	}

	_buildControl(ctx: IControlLabelContext & IRegPointer<IChainEnv | IWspUiEnv>, name: string): HTMLDivElement {
		let rootElt: HTMLDivElement = <div/> as HTMLDivElement;

		let isInDrfWspCtx: true | undefined = undefined;
		let isInDrvWspCtx: true | undefined = undefined;
		if ((ctx as IRegPointer<IWspUiEnv>).reg.env.wsp) {
			const wspDefProps = (ctx as IRegPointer<IWspUiEnv>).reg.env.wsp.wspDefProps;
			if (wspDefProps?.drfRefWsp) isInDrfWspCtx = true;
			if (wspDefProps?.drvAxis) isInDrvWspCtx = true;
		}

		function fAssignFormInput(input: HTMLInputElement) {
			Object.assign(input, {
				fillJson: function (parent: Jsonisable, root: Jsonisable) {
					parent[input.name] = input.checked ? true : false;
				},
				extractJson: function (parent: Dict<any>) {
					const value = parent[input.name];
					input.checked = value == true ? true : false;
					if (input.parentElement.classList.contains("deprecated"))
						DOM.setHidden(input.parentElement, !input.checked);
					return true;
				}
			} as IFormJsonisable);
		}

		// publicWsp
		const publicWsp = ctx.reg.env.universe.getWspFeatureStatus("publicWsp");
		if (!this.noPublicWsp && publicWsp && publicWsp != "alwaysFalse") {
			let id = this.getId() + "~publicWsp";
			const inputElt = <input name="publicWsp" id={id} type="checkbox" disabled={this.isReadOnly(ctx) || publicWsp.startsWith('always') ? true : null}
															checked={publicWsp.toLocaleLowerCase().indexOf("True") >= 0 ? true : null}/> as HTMLInputElement;
			rootElt.appendChild(<div class={`optionslist ${publicWsp == 'deprecated' ? 'deprecated' : ''}`}
															 title="Passe cet atelier dans un statut 'public'. Tout utilisateur connecté dispose d'un accès en lecture à un atelier dit 'public'. Cette option doit être activée pour permettre à d'autres ateliers d'y référencer des items étrangers (liens directs).">
				{inputElt}
				<label for={id}>Atelier public</label>
				{publicWsp === 'deprecated' ? <span class="titleDeprecated">fonction dépréciée</span> : ''}
			</div>);
			fAssignFormInput(inputElt);
		}
		// extIt
		const extIt = ctx.reg.env.universe.getWspFeatureStatus("extIt");
		if (!this.noExtIt && !isInDrfWspCtx && !isInDrvWspCtx && extIt != "alwaysFalse") {
			let id = this.getId() + "~extIt";
			const inputElt = <input name="extIt" id={id} type="checkbox" disabled={this.isReadOnly(ctx) || extIt.startsWith('always') ? true : null} checked={extIt.toLocaleLowerCase().indexOf("True") >= 0 ? true : null}/> as HTMLInputElement;
			rootElt.appendChild(<div class={`optionslist ${extIt == 'deprecated' ? 'deprecated' : ''}`}
															 title="Active les fonctions de liens inter-ateliers directs (items étrangers). Cela permet aux items de cet atelier de référencer des items d'un autre atelier 'public'.">
				{inputElt}
				<label for={id}>Liens inter-ateliers directs</label>
				{extIt === 'deprecated' ? <span class="titleDeprecated">fonction dépréciée</span> : ''}
			</div>);
			fAssignFormInput(inputElt);
		}
		// airIt
		const airIt = ctx.reg.env.universe.getWspFeatureStatus("airIt");
		if (!this.noAirIt && !isInDrfWspCtx && !isInDrvWspCtx && airIt && airIt != "alwaysFalse") {
			let id = this.getId() + "~airIt";
			const inputElt = <input name="airIt" id={id} type="checkbox" disabled={this.isReadOnly(ctx) || airIt.startsWith('always') ? true : null} checked={airIt.toLocaleLowerCase().indexOf("True") >= 0 ? true : null}/> as HTMLInputElement;
			rootElt.appendChild(<div class={`optionslist ${airIt == 'deprecated' ? 'deprecated' : ''}`}
															 title="Active les fonctions liées aux items 'flottants' (non ancrés dans un espace).">
				{inputElt}
				<label for={id}>Items 'flottants'</label>
				{airIt === 'deprecated' ? <span class="titleDeprecated">fonction dépréciée</span> : ''}
			</div>);
			fAssignFormInput(inputElt);
		}

		return rootElt;
	}

	isVisible(ctx: IControlLabelContext & IRegPointer<IChainEnv>): boolean {
		if ((!ctx.reg.env.universe.getWspFeatureStatus("airIt") || ctx.reg.env.universe.getWspFeatureStatus("airIt") == "alwaysFalse")
			&& (!ctx.reg.env.universe.getWspFeatureStatus("extIt") || ctx.reg.env.universe.getWspFeatureStatus("extIt") == "alwaysFalse")
			&& (!ctx.reg.env.universe.getWspFeatureStatus("airIt") || ctx.reg.env.universe.getWspFeatureStatus("airIt") == "alwaysFalse")
		)
			return false;
		return super.isVisible(ctx);
	}
}

/**
 * [wsps edit] Déclencher la migration
 */
class DoMigrateIfNecessaryArea extends InputChoiceArea<IRegPointer<IWspUiEnv>> {
	constructor() {
		super("doMigrateIfNecessary", "checkbox");
		this.setLabel("Migration").setDataset({"true": {label: "Déclencher la migration si pertinent"}});
		this.setDefaultValue("true");
	}
}

type WspTypeInstNewContext = IControlLabelContext & IRegPointer<IChainEnv> & (IRegPointer<IWspUiEnv> | IRegsPointer<IWspUiEnv>);

/**
 * WspTypeInst : création
 */
class WspTypeInstNewInputArea extends ControlAsyncArea<WspTypeInstNewContext> {
	constructor() {
		super("wspType");
		this.setLabel("Modèle");
		this.setRequired(true);
	}

	async _loadControl(ctx: WspTypeInstNewContext): Promise<HTMLElement> {
		let fromWsp = ctx.reg && 'wsp' in ctx.reg.env && ctx.reg.env.wsp;
		let wspTypeSelector = new WspTypeSelector().initialize({
			mode: "new",
			reg: ctx.reg,
			fromWspCode: fromWsp ? fromWsp.code : null,
			name: this._id,
			disabled: !this.isEnabled(ctx),
			required: this.isRequired(ctx)
		});
		wspTypeSelector.className = "formElt";
		return wspTypeSelector.initializedAsync;
	}
}

type WspTypeInstEditContext = IControlLabelContext & IRegPointer<IWspUiEnv>;

/**
 * WspTypeInst : édition
 */
class WspTypeInstEditArea extends ControlAsyncArea<WspTypeInstEditContext> {
	constructor() {
		super("wspType");
		this.setLabel("Modèle");
	}

	async _loadControl(ctx: WspTypeInstEditContext, name: string): Promise<HTMLElement> {
		let wspTypeSelector = new WspTypeSelector().initialize({
			mode: "edit",
			reg: ctx.reg,
			fromWspCode: ctx.reg.env.wsp?.code,
			name: this._id,
			disabled: !this.isEnabled(ctx),
			required: this.isRequired(ctx),
			readonly: this.isReadOnly(ctx)
		});
		wspTypeSelector.className = "formElt";
		return wspTypeSelector.initializedAsync;
	}

	isReadOnly(ctx: WspTypeInstEditContext): boolean {
		return !ctx.reg.hasPerm("action.wspProps#update.wsptype");
	}
}


/**
 * admin wsp
 */
class AdminWspToolbarArea extends LabelArea {
	constructor(id: string) {
		super(id);
		this.setLabel("Administration de l'atelier");
		this.requireVisiblePerm("action.wspProps#admin.wsp");
	}

	_buildControl(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>, name: string): HTMLElement {
		return <c-bar-actions î={{
			reg: REG.getReg(ctx),
			actions: REG.getReg(ctx).getList<Action<IRegPointer<IWspUiEnv>>>(`actions:wspProps:${this.getId()}`),
			actionContext: {
				reg: REG.getReg(ctx),
			} as IRegPointer<IWspUiEnv>,
			disableFullOverlay: true,
			uiContext: "dialog",
		} as OBarActionsInit<IRegPointer<IWspUiEnv>>} id="actions" hidden="true"/>;
	}

}

//c-action

/**
 * Skinset
 * Contexte : Wsp
 */
export class SkinsetEditInputArea extends ControlAsyncArea<IControlLabelContext & IRegPointer<IWspUiEnv>> {

	constructor() {
		super("skins");
		this.setLabel("Habillages graphiques");
		this.setDescription("Habillages graphiques des générateurs du modèle documentaire");
	}

	async loadBody(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): Promise<HTMLElement> {
		let body = await super.loadBody(ctx, lastDatas);
		if (!await WSP.checkWspSvc(ctx.reg.env.wsp, body, "skinSet"))
			DOM.setHidden(body, true)
		return body;
	}

	async _loadControl(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>, name: string): Promise<HTMLElement> {
		const skinsetSelector = new SkinSetSelector().initialize({
			reg: ctx.reg,
			wsp: ctx.reg.env.wsp,
			propsJsonName: this._id,
			disabled: !this.isEnabled(ctx),
		});
		return skinsetSelector.initializedAsync;
	}

	isEnabled(ctx: IControlLabelContext & IRegPointer<IWspUiEnv>): boolean {
		return ctx.reg.hasPerm("action.wspProps#update.wsptype");
	}
}

function updatePropReadonly(ctx: IRegPointer<IWspUiEnv>) {
	return !ctx.reg.hasPerm("action.wspProps#update.props");
}

function fromWspPropReadonly(ctx: IRegPointer<IWspUiEnv>) {
	return !ctx.reg.env.wsp.isLoaded;
}


/**
 * Déclaration des Areas dans les différentes cibles standards
 * 		'wspProps:create:commons',
 * 			'wspProps:create:fromScwsp', 'wspProps:create:fromScratch',
 * 			'wspProps:create:fs', 'wspProps:create:db', 'wspProps:create:db:other', 'wspProps:create:db:drv', 'wspProps:create:db:drv:l10n', 'wspProps:create:db:drf'
 * 		'wspProps:edit:commons:infos', 'wspProps:edit:fs:infos', 'wspProps:edit:db:infos', 'wspProps:edit:db:other:infos', 'wspProps:edit:db:drv:infos', 'wspProps:edit:db:drf:infos', 'wspProps:edit:db:l10n:infos'
 * 		'wspProps:edit:commons:advanced', 'wspProps:edit:fs:advanced', 'wspProps:edit:db:advanced', 'wspProps:edit:db:other:advanced', 'wspProps:edit:db:drv:advanced', 'wspProps:edit:db:drf:advanced', 'wspProps:edit:db:l10n:advanced'
 *		'wspProps:editAll:commons', 'wspProps:editAll:fs', 'wspProps:editAll:db', 'wspProps:editAll:db:drv', 'wspProps:editAll:db:drf', 'wspProps:editAll:db:l10n',
 * 		'wspProps:remove:commons', 'wspProps:remove:fs', 'wspProps:remove:db', 'wspProps:remove:db:other', 'wspProps:remove:db:drv', 'wspProps:remove:db:drf', 'wspProps:remove:db:l10n'
 *		'wspProps:removeAll:commons', 'wspProps:removeAll:fs', 'wspProps:removeAll:db'
 **/

// *
// - Create
REG.reg.addToList('wspProps:create:fromScratch', 'WspTypeInstInputArea', 1, new WspTypeInstNewInputArea(), 101);
REG.reg.addToList('wspProps:create:fromScwsp', 'WspSelectScarInputArea', 1, new WspSelectScarInputArea().setRequired(true), 101);
// - Edit
REG.reg.addToList('wspProps:edit:commons:infos', 'WspStatusArea', 1, new WspStatusArea(), 31);
REG.reg.addToList('wspProps:edit:commons:infos', 'WspTypeInstEditArea', 1, new WspTypeInstEditArea(), 101);

REG.reg.addToList('wspProps:edit:commons:advanced', 'AdminWspToolbarArea', 1, new AdminWspToolbarArea("adminWsp"), 51);
REG.reg.addToList("actions:wspProps:adminWsp", "ReloadWspAction", 1, new ReloadWspAction().setLabel("Recharger"));
REG.reg.addToList("actions:wspProps:adminWsp", "RemoveCacheWspAction", 1, new RemoveCacheWspAction().setLabel("Vider le cache"));
// - EditAll
REG.reg.addToList('wspProps:editAll:commons', 'DoMigrateIfNecessaryArea', 1, new DoMigrateIfNecessaryArea(), 101);
REG.reg.addToList('wspProps:editAll:commons', 'WspTypeInstEditArea', 1, new WspTypeInstEditArea(), 102);

// - Remove
REG.reg.addToList('wspProps:remove:commons', 'WspDeleteGenInputArea', 1, new WspDeleteGenInputArea(), 21);
// - RemoveAll
REG.reg.addToList('wspProps:removeAll:commons', 'WspDeleteGenInputArea', 1, new WspDeleteGenInputArea().setLabel("Supprimer les répertoires de génération"), 21);

// FS
// - Create
REG.reg.addToList('wspProps:create:fs', 'WspCodeInputArea', 1, new WspCodeInputArea(), 11);
REG.reg.addToList('wspProps:create:fs', 'WspTitleInputArea', 1, new WspTitleInputArea(true), 21);
// - Edit
REG.reg.addToList('wspProps:edit:fs:infos', 'WspCodeArea', 1, new WspCodeArea(), 11);
REG.reg.addToList('wspProps:edit:fs:infos', 'WspTitleInputArea', 1, new WspTitleInputArea(), 21);
// - Remove
REG.reg.addToList('wspProps:remove:fs', 'WspDeleteFsInputArea', 1, new WspDeleteFsInputArea(), 11);
// - RemoveAll
REG.reg.addToList('wspProps:removeAll:fs', 'WspDeleteFsInputArea', 1, new WspDeleteFsInputArea().setLabel("Supprimer les répertoires des données"), 21);

// DB * (only, drv, drf, ...)
// - Create
REG.reg.addToList('wspProps:create:db', 'WspDescInputArea', 1, new WspDescInputArea(), 41);

// - Edit
REG.reg.addToList('wspProps:edit:db:infos', 'WspDescInputArea', 1, new WspDescInputArea().setReadOnly(updatePropReadonly), 41);
REG.reg.addToList('wspProps:edit:db:advanced', 'WspCodeArea', 1, new WspCodeArea(), 21);// Juste pour information
REG.reg.addToList('wspProps:edit:db:infos', 'WspOptionsInputArea', 1, new WspOptionsInputArea().setReadOnly(updatePropReadonly), 51);

// DB only
// - Create
REG.reg.addToList('wspProps:create:db:other', 'WspTitleInputArea', 1, new WspTitleInputArea().setRequired(true), 21);
REG.reg.addToList('wspProps:create:db:other', 'WspOptionsInputArea', 1, new WspOptionsInputArea(), 51);

// - Edit
REG.reg.addToList('wspProps:edit:db:other:infos', 'WspTitleInputArea', 1, new WspTitleInputArea().setRequired(true).setReadOnly(updatePropReadonly), 21);
REG.reg.addToList('wspProps:edit:db:other:skins', 'SkinsetEditInputArea', 1, new SkinsetEditInputArea(), 61);

// DB - DRF
// - Create
REG.reg.addToList('wspProps:create:db:drf', 'WspDraftTitleInputArea', 1, new WspDraftTitleInputArea().setRequired(true).setReadOnly(fromWspPropReadonly), 21);
REG.reg.addToList('wspProps:create:db:drf', 'WspOptionsInputArea', 1, new WspOptionsInputArea(false, true, true), 51);
// - Edit
REG.reg.addToList('wspProps:edit:db:drf:infos', 'WspDrfRefWspTitleArea', 1, new WspDrfRefWspTitleArea(), 1);
REG.reg.addToList('wspProps:edit:db:drf:infos', 'WspDraftTitleInputArea', 1, new WspDraftTitleInputArea().setRequired(true).setReadOnly(updatePropReadonly), 11);
REG.reg.addToList('wspProps:edit:db:drf:advanced', 'WspDrfRefWspArea', 1, new WspDrfRefWspArea(), 21);

// DB - DRV
// - Create
REG.reg.addToList('wspProps:create:db:drv', 'WspDrvAxisInputArea', 1, new WspDrvAxisInputArea().setRequired(true).setReadOnly(fromWspPropReadonly), 31);
//REG.reg.addToList('wspProps:create:db:drv:l10n', 'WspDrvAxisInputArea', 2, new WspDrvAxisInputArea().setRequired(true).setLabel("Code de traduction").setReadOnly(fromWspPropReadonly), 31);
REG.reg.addToList('wspProps:create:db:drv', 'WspDrvDefaultSrcFindPathInputArea', 1, new WspDrvDefaultSrcFindPathInputArea().setReadOnly(fromWspPropReadonly), 41);
REG.reg.addToList('wspProps:create:db:drv', 'WspOptionsInputArea', 1, new WspOptionsInputArea(false, true, true), 51);
// - Edit
REG.reg.addToList('wspProps:edit:db:drv:infos', 'WspDrvMasterTitleArea', 1, new WspDrvMasterTitleArea(), 1);
REG.reg.addToList('wspProps:edit:db:drv:infos', 'WspTitleInputArea', 1, new WspTitleInputArea().setReadOnly(true), 21);
REG.reg.addToList('wspProps:edit:db:drv:infos', 'WspDrvDefaultSrcFindPathInputArea', 1, new WspDrvDefaultSrcFindPathInputArea().setReadOnly(updatePropReadonly), 41);
REG.reg.addToList('wspProps:edit:db:drv:advanced', 'WspDrvMasterArea', 1, new WspDrvMasterArea(), 21);

// DB - l10n
// - Edit
REG.reg.addToList('wspProps:edit:db:l10n:infos', 'WspL10nInputArea', 1, new WspL10nInputArea(), 35);
